import {
  getFileTypeFromPath,
  getHumanReadableSize,
} from '~/composer/composer/utils/StringUtils';
import { UppyFile } from '~/uploads/uppy/UppyFile';
import { Upload } from '~/uploads/entities/Upload';
import { UploaderRestrictions } from '~/uploads/values/UploaderRestrictions';

export type FileRejection = {
  message: string;
  file: File;
};

type FileValidationResult = {
  validFiles: File[];
  invalidFiles: Array<FileRejection>;
};

type FileValidationOptions = {
  fileRestrictions: UploaderRestrictions;
  currentFiles: Array<UppyFile | Upload>;
};

export const validateFile = async (
  file: File,
  { fileRestrictions: { uploadConfig } }: FileValidationOptions
): Promise<File | FileRejection> => {
  const fileFormat = getFileTypeFromPath(file.name).toUpperCase();

  if (!uploadConfig.has(fileFormat)) {
    const acceptedFilesText = Array.from(uploadConfig.keys()).join(', ');
    const message = `We can't quite use that type of file (${fileFormat.toUpperCase()}). Could you try one of the following instead: ${acceptedFilesText}?`;

    return {
      message,
      file,
    };
  }

  const maxSize = uploadConfig.get(fileFormat)?.maxSize || 0;

  if (file.size > maxSize) {
    return {
      message: `We can't upload ${
        file.name
      } because it's too large. Try a file smaller than ${getHumanReadableSize(
        maxSize
      )}.`,
      file,
    };
  }

  return file;
};

export const validateFilesForUploads = async (
  files: File[],
  validationOptions: FileValidationOptions
): Promise<FileValidationResult> => {
  const validFiles: FileValidationResult['validFiles'] = [];
  const invalidFiles: FileValidationResult['invalidFiles'] = [];
  const maxNumberOfFiles =
    validationOptions.fileRestrictions?.maxNumberOfFiles || 10;
  const currentFiles = validationOptions.currentFiles || [];
  const totalFiles = currentFiles.length + files.length;
  const totalAllowedToUpload = maxNumberOfFiles - currentFiles.length;

  if (totalFiles > maxNumberOfFiles) {
    const removedFiles = files.splice(totalAllowedToUpload);
    invalidFiles.push({
      message: `We can only upload ${maxNumberOfFiles} files in total.`,
      file: removedFiles[0],
    });
  }

  const filesToUpload = files.splice(0, totalAllowedToUpload);
  const validationResults = await Promise.all(
    filesToUpload.map((file) => validateFile(file, validationOptions))
  );

  validationResults.forEach((result) => {
    if (result instanceof File) {
      validFiles.push(result);
    } else {
      invalidFiles.push(result);
    }
  });

  return {
    validFiles,
    invalidFiles,
  };
};
